import * as React from "react";
import {
    Button,
    Form,
    FormGroup,
    FormAlert,
    Alert
} from "@patternfly/react-core";
import {useState, useEffect} from "react";
import {updateCampaign} from "@app/utils/utils";
import {
    MapMarkerIcon,
    UsersIcon,
    CheckCircleIcon,
    RouteIcon,
    EditIcon
} from "@patternfly/react-icons";
import { GeoFenceMapModal } from './GeoFenceMapModal';
import { DrawRouteMapModal } from './DrawRouteMapModal';
import { GeoFenceLocation } from './types/geoFence';
import { DrawRoutesConfig } from './types/drawRoutes';
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { BorderedInput } from '@app/Login/BorderedInput';
import { PURPLE_GRADIENT, colors, radii, fieldLabelStyle, formResetStyle } from '@app/components/designTokens';

export interface IEditCampaignProps {
    campaign: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export const EditCampaign: React.FunctionComponent<IEditCampaignProps> = (props) => {
    const [name, setName] = useState('');
    const [type, setType] = useState('GeoFenceCampaign');
    const [validated, setValidated] = useState('default');
    const [isLoading, setIsLoading] = useState(false);
    const [geoFenceLocation, setGeoFenceLocation] = useState<GeoFenceLocation | null>(null);
    const [showGeoFenceModal, setShowGeoFenceModal] = useState(false);
    const [drawRoutesConfig, setDrawRoutesConfig] = useState<DrawRoutesConfig | null>(null);
    const [showDrawRouteModal, setShowDrawRouteModal] = useState(false);

    // Pre-populate form when campaign changes
    useEffect(() => {
        if (props.campaign) {
            setName(props.campaign.name || '');
            setType(props.campaign.type || 'GeoFenceCampaign');
            setValidated('default');

            // Load geo fence data if available
            const config = props.campaign.config || {};
            if (config.geoFence) {
                setGeoFenceLocation(config.geoFence);
            } else {
                setGeoFenceLocation(null);
            }

            // Load draw routes data if available
            if (config.drawRoutes) {
                setDrawRoutesConfig(config.drawRoutes);
            } else {
                setDrawRoutesConfig(null);
            }
        }
    }, [props.campaign]);

    const handleClose = () => {
        setValidated('default');
        props.onClose();
    };

    function validateFields() {
        if (!name || !type) {
            return false;
        }
        return true;
    }

    async function saveCampaign() {
        if (validateFields()) {
            setIsLoading(true);
            try {
                // Build config with geo fence and draw routes data
                const config = { ...(props.campaign.config || {}) };
                if (type === 'GeoFenceCampaign' && geoFenceLocation) {
                    config.geoFence = geoFenceLocation;
                } else if (type !== 'GeoFenceCampaign') {
                    // Remove geo fence data if type is changed to non-GeoFence
                    delete config.geoFence;
                }

                if (type === 'DrawRoutesCampaign' && drawRoutesConfig) {
                    config.drawRoutes = drawRoutesConfig;
                } else if (type !== 'DrawRoutesCampaign') {
                    // Remove draw routes data if type is changed to non-DrawRoutes
                    delete config.drawRoutes;
                }

                await updateCampaign(props.campaign.campaignId, {
                    "name": name,
                    "type": type,
                    "ads": props.campaign.ads || [],
                    "config": config
                });
                props.onSave();
                handleClose();
            } catch (error) {
                setValidated('error');
            } finally {
                setIsLoading(false);
            }
        } else {
            setValidated('error');
        }
    }

    const styles = {
        body: { padding: '40px 32px 32px' } as React.CSSProperties,
        headerBlock: { textAlign: 'center' as const, marginBottom: '24px' },
        title: { color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' },
        subtitle: { color: colors.subtle, fontSize: '14px', margin: 0 },
        typeCardSelected: {
            display: 'flex',
            alignItems: 'center',
            padding: '14px 16px',
            backgroundColor: '#f5f3ff',
            borderRadius: radii.input,
            cursor: 'pointer',
            transition: 'all 0.15s',
            border: `1px solid ${colors.accent}`,
            gap: '12px',
            boxShadow: `0 0 0 3px ${colors.accentRing}`
        } as React.CSSProperties,
        typeIcon: {
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '16px',
            flexShrink: 0
        },
        typeLabel: {
            fontSize: '15px',
            fontWeight: 500,
            color: colors.title
        },
        actionsContainer: {
            marginTop: '24px',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '10px'
        },
        cancelButton: {
            color: colors.subtle,
            fontWeight: 600,
            textAlign: 'center' as const,
            fontSize: '14px'
        }
    };

    const isFormValid = name && type;
    const isGeoFenceCampaign = type === 'GeoFenceCampaign';
    const isDrawRoutesCampaign = type === 'DrawRoutesCampaign';
    const isDraft = props.campaign?.currentState === 'DRAFT';

    if (!props.isOpen) {
        return null;
    }

    return (
        <AppModal isOpen={props.isOpen} onClose={handleClose} width="md">
            <div style={styles.body}>
                <div style={styles.headerBlock}>
                    <LogoBadge size={48} />
                    <h1 style={styles.title}>Edit campaign</h1>
                    <p style={styles.subtitle}>Update your campaign information</p>
                </div>

                    <Form style={formResetStyle}>
                        {validated === 'error' && (
                            <FormAlert>
                                <Alert
                                    variant="danger"
                                    title="Please fill out all required fields."
                                    aria-live="polite"
                                    isInline
                                />
                            </FormAlert>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                            <label htmlFor="edit-campaign-name" style={fieldLabelStyle}>
                                Campaign Name<span style={{ color: colors.danger, marginLeft: 2 }}>*</span>
                            </label>
                            <BorderedInput
                                id="edit-campaign-name"
                                value={name}
                                onChange={setName}
                                placeholder="Enter campaign name"
                                icon={<EditIcon />}
                            />
                            {!isDraft && (
                                <div style={{ fontSize: '12px', color: colors.subtle, marginTop: '4px' }}>
                                    Campaign name can only be changed while in DRAFT state
                                </div>
                            )}
                        </div>

                        <FormGroup
                            label="Campaign Type"
                            fieldId="edit-campaign-type"
                        >
                            <div style={{
                                ...styles.typeCardSelected,
                                cursor: 'default'
                            }}>
                                {type === 'GeoFenceCampaign' && (
                                    <>
                                        <div style={styles.typeIcon}>
                                            <MapMarkerIcon />
                                        </div>
                                        <span style={styles.typeLabel}>Geo Fence</span>
                                    </>
                                )}
                                {type === 'DrawRoutesCampaign' && (
                                    <>
                                        <div style={styles.typeIcon}>
                                            <RouteIcon />
                                        </div>
                                        <span style={styles.typeLabel}>Draw Route</span>
                                    </>
                                )}
                                {type === 'ProjectedReachCampaign' && (
                                    <>
                                        <div style={styles.typeIcon}>
                                            <UsersIcon />
                                        </div>
                                        <span style={styles.typeLabel}>Target Customer Reach</span>
                                    </>
                                )}
                            </div>
                            <div style={{ fontSize: '12px', color: colors.subtle, marginTop: '4px' }}>
                                Campaign type cannot be changed after creation
                            </div>
                        </FormGroup>

                        {isGeoFenceCampaign && (
                            <FormGroup
                                label="Geo Fence Location"
                                fieldId="edit-campaign-geofence"
                            >
                                {geoFenceLocation ? (
                                    <div
                                        style={{
                                            border: `1px solid ${colors.accent}`,
                                            borderRadius: radii.input,
                                            padding: '14px 16px',
                                            background: '#f5f3ff',
                                            boxShadow: `0 0 0 3px ${colors.accentRing}`,
                                            cursor: isDraft ? 'pointer' : 'default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            opacity: isDraft ? 1 : 0.8
                                        }}
                                        onClick={isDraft ? () => setShowGeoFenceModal(true) : undefined}
                                    >
                                        <div style={styles.typeIcon}>
                                            <MapMarkerIcon />
                                        </div>
                                        <div style={{flex: 1}}>
                                            <div style={{fontSize: '14px', fontWeight: 600, color: colors.title}}>
                                                {geoFenceLocation.address}
                                            </div>
                                            <div style={{fontSize: '12px', color: colors.subtle}}>
                                                Radius: {geoFenceLocation.radius}m
                                            </div>
                                        </div>
                                        <CheckCircleIcon style={{color: colors.accent, fontSize: '20px'}} />
                                    </div>
                                ) : isDraft ? (
                                    <div
                                        style={{
                                            border: `2px dashed ${colors.border}`,
                                            borderRadius: radii.input,
                                            padding: '20px',
                                            textAlign: 'center' as const,
                                            cursor: 'pointer',
                                            background: '#fafafa'
                                        }}
                                        onClick={() => setShowGeoFenceModal(true)}
                                    >
                                        <MapMarkerIcon style={{fontSize: '24px', color: colors.accent, marginBottom: '8px'}} />
                                        <div style={{fontSize: '14px', color: colors.subtle}}>
                                            Click to select location
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            border: `2px dashed ${colors.border}`,
                                            borderRadius: radii.input,
                                            padding: '20px',
                                            textAlign: 'center' as const,
                                            opacity: 0.7
                                        }}
                                    >
                                        <MapMarkerIcon style={{fontSize: '24px', color: colors.placeholder, marginBottom: '8px'}} />
                                        <div style={{fontSize: '14px', color: colors.subtle}}>
                                            No location set
                                        </div>
                                    </div>
                                )}
                                {!isDraft && (
                                    <div style={{ fontSize: '12px', color: colors.subtle, marginTop: '4px' }}>
                                        Location can only be changed while in DRAFT state
                                    </div>
                                )}

                                {isDraft && (
                                    <GeoFenceMapModal
                                        isOpen={showGeoFenceModal}
                                        onClose={() => setShowGeoFenceModal(false)}
                                        onConfirm={(location) => {
                                            setGeoFenceLocation(location);
                                            setShowGeoFenceModal(false);
                                        }}
                                        initialLocation={geoFenceLocation || undefined}
                                    />
                                )}
                            </FormGroup>
                        )}

                        {isDrawRoutesCampaign && (
                            <FormGroup
                                label="Route"
                                fieldId="edit-campaign-drawroutes"
                            >
                                {drawRoutesConfig ? (
                                    <div
                                        style={{
                                            border: `1px solid ${colors.accent}`,
                                            borderRadius: radii.input,
                                            padding: '14px 16px',
                                            background: '#f5f3ff',
                                            boxShadow: `0 0 0 3px ${colors.accentRing}`,
                                            cursor: isDraft ? 'pointer' : 'default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            opacity: isDraft ? 1 : 0.8
                                        }}
                                        onClick={isDraft ? () => setShowDrawRouteModal(true) : undefined}
                                    >
                                        <div style={styles.typeIcon}>
                                            <RouteIcon />
                                        </div>
                                        <div style={{flex: 1}}>
                                            <div style={{fontSize: '14px', fontWeight: 600, color: colors.title}}>
                                                {drawRoutesConfig.startingPoint.address}
                                            </div>
                                            <div style={{fontSize: '12px', color: colors.subtle}}>
                                                {drawRoutesConfig.waypoints.length} waypoints
                                                {drawRoutesConfig.totalDistanceText && ` \u2022 ${drawRoutesConfig.totalDistanceText}`}
                                            </div>
                                        </div>
                                        <CheckCircleIcon style={{color: colors.accent, fontSize: '20px'}} />
                                    </div>
                                ) : isDraft ? (
                                    <div
                                        style={{
                                            border: `2px dashed ${colors.border}`,
                                            borderRadius: radii.input,
                                            padding: '20px',
                                            textAlign: 'center' as const,
                                            cursor: 'pointer',
                                            background: '#fafafa'
                                        }}
                                        onClick={() => setShowDrawRouteModal(true)}
                                    >
                                        <RouteIcon style={{fontSize: '24px', color: colors.accent, marginBottom: '8px'}} />
                                        <div style={{fontSize: '14px', color: colors.subtle}}>
                                            Click to draw route
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            border: `2px dashed ${colors.border}`,
                                            borderRadius: radii.input,
                                            padding: '20px',
                                            textAlign: 'center' as const,
                                            opacity: 0.7
                                        }}
                                    >
                                        <RouteIcon style={{fontSize: '24px', color: colors.placeholder, marginBottom: '8px'}} />
                                        <div style={{fontSize: '14px', color: colors.subtle}}>
                                            No route set
                                        </div>
                                    </div>
                                )}
                                {!isDraft && (
                                    <div style={{ fontSize: '12px', color: colors.subtle, marginTop: '4px' }}>
                                        Route can only be changed while in DRAFT state
                                    </div>
                                )}

                                {isDraft && (
                                    <DrawRouteMapModal
                                        isOpen={showDrawRouteModal}
                                        onClose={() => setShowDrawRouteModal(false)}
                                        onConfirm={(config) => {
                                            setDrawRoutesConfig(config);
                                            setShowDrawRouteModal(false);
                                        }}
                                        initialConfig={drawRoutesConfig || undefined}
                                    />
                                )}
                            </FormGroup>
                        )}

                        <div style={styles.actionsContainer}>
                            <GradientButton
                                onClick={saveCampaign}
                                isLoading={isLoading}
                                isDisabled={!isFormValid || isLoading}
                                isBlock
                            >
                                {isLoading ? 'Saving changes...' : 'Save changes'}
                            </GradientButton>
                            <Button
                                variant="link"
                                onClick={handleClose}
                                style={styles.cancelButton}
                                isBlock
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
            </div>
        </AppModal>
    );
};
